import type { FC, ReactNode } from 'react'

import { useEffect, useRef } from 'react'
import { useRouter } from 'next/router'
import { cn } from '@/lib/utils'

interface ProseProps {
    html: string
    className?: string
}

/**
 * Renders build-time HTML from the markdown pipeline. After mount it turns
 * on the copy buttons in code listings (they stay hidden without JS).
 */
const Prose: FC<ProseProps> = ({ html, className }): ReactNode => {
    const ref = useRef<HTMLDivElement>(null)
    const router = useRouter()

    /* Internal links inside the article navigate client-side, like next/link. */
    useEffect(() => {
        const root = ref.current

        if (!root) return

        const onNavigate = (event: MouseEvent) => {
            if (event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return

            const anchor = (event.target as HTMLElement | null)?.closest<HTMLAnchorElement>('a[href^="/"]')

            if (!anchor || !root.contains(anchor) || anchor.target === '_blank' || anchor.hasAttribute('download')) return
            if (/\.(xml|txt|pdf|zip)$/i.test(anchor.pathname)) return

            event.preventDefault()
            void router.push(anchor.getAttribute('href') ?? anchor.pathname)
        }

        root.addEventListener('click', onNavigate)

        return () => root.removeEventListener('click', onNavigate)
    }, [router])

    useEffect(() => {
        const root = ref.current

        if (!root) return

        const timers = new Set<number>()

        root.setAttribute('data-enhanced', '')

        const reset = (button: HTMLButtonElement, label: string) => {
            const timer = window.setTimeout(() => {
                button.removeAttribute('data-copied')
                button.textContent = 'Copy'
                button.setAttribute('aria-label', label)
                timers.delete(timer)
            }, 2000)

            timers.add(timer)
        }

        const onClick = async (event: MouseEvent) => {
            const target = event.target as HTMLElement | null
            const button = target?.closest<HTMLButtonElement>('button[data-copy]')

            if (!button || !root.contains(button)) return

            const label = button.getAttribute('aria-label') ?? 'Copy code'
            const code = button.closest('figure')?.querySelector('pre')?.textContent ?? ''

            try {
                await navigator.clipboard.writeText(code)
                button.setAttribute('data-copied', '')
                button.textContent = 'Copied'
                button.setAttribute('aria-label', 'Copied to clipboard')
            }
            catch {
                button.textContent = 'Select and copy'
            }

            reset(button, label)
        }

        root.addEventListener('click', onClick)

        return () => {
            root.removeEventListener('click', onClick)
            timers.forEach((timer) => window.clearTimeout(timer))
        }
    }, [html])

    return (
        <div
            ref={ref}
            className={cn('prose-editorial', className)}
            dangerouslySetInnerHTML={{ __html: html }}
        />
    )
}

export default Prose
